import { useState, useMemo, useEffect } from "react";

/**
 * Tiny client-side pagination hook.
 *
 * Use on admin list pages that don't go through `<DataTable />` (which already
 * has pagination baked-in). Pass any rows array; get back a sliced `visible`
 * array, the current `page`, a `setPage` setter and the totals — wire those
 * into the existing `<Pagination />` component:
 *
 *     const { visible, page, setPage, totalRows, pageSize } = usePagedRows(rows);
 *     ...
 *     {visible.map(...)}
 *     <Pagination total={totalRows} page={page} pageSize={pageSize} onChange={setPage} />
 *
 * `keyDeps` is an optional list of values whose change should reset the page
 * back to 1 (e.g. when filters change).
 */
export default function usePagedRows(rows, pageSize = 12, keyDeps = []) {
  const [page, setPage] = useState(1);
  const safeRows = Array.isArray(rows) ? rows : [];
  const totalRows = safeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Reset to page 1 when external filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, keyDeps);

  // Clamp page if dataset shrinks below current page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages]);

  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return safeRows.slice(start, start + pageSize);
  }, [safeRows, page, pageSize]);

  return { visible, page, setPage, totalRows, totalPages, pageSize };
}
